/**
 * Difficulty formulas.
 *
 * RULE DCC-DIFF-004 - Rules for Survival p.13: the formulas below are
 * guidelines. The GM may assign any Difficulty instead, and the GM does not
 * have to reveal it. The application therefore never requires a Difficulty.
 */
export type DifficultyBasis = 'opposed' | 'unopposed' | 'stat-check' | 'gm-assigned';

export interface DifficultyResult {
  basis: DifficultyBasis;
  value: number;
  /** Plain-language derivation for display. */
  breakdown: string;
}

/**
 * RULE DCC-DIFF-001 - Opposed Check: 10 + antagonist's relevant Stat Mod + Floor Number.
 * Source: Rules for Survival p.13.
 * The Stat Mod term may be replaced by a Spell Rank, a size class or another
 * opposing force, so the caller supplies the number rather than a Stat.
 */
export function opposedDifficulty(antagonistMod: number, floor: number): DifficultyResult {
  assertFloor(floor);
  return {
    basis: 'opposed',
    value: 10 + antagonistMod + floor,
    breakdown: `10 + ${antagonistMod} (antagonist) + ${floor} (Floor)`,
  };
}

/**
 * RULE DCC-DIFF-002 - Unopposed Check: 10 + (Floor Number x 2).
 * Source: Rules for Survival p.14.
 */
export function unopposedDifficulty(floor: number): DifficultyResult {
  assertFloor(floor);
  return {
    basis: 'unopposed',
    value: 10 + floor * 2,
    breakdown: `10 + (${floor} x 2) (Floor)`,
  };
}

/**
 * RULE DCC-DIFF-003 - Stat Check Difficulty: 10 + Floor.
 * Source: Rules for Survival p.14.
 * See docs/RULES_CONFLICTS.md CONFLICT-003: p.13 gives two example Stat Checks
 * that use a different formula. Those are treated as GM-assigned overrides.
 */
export function statCheckDifficulty(floor: number): DifficultyResult {
  assertFloor(floor);
  return {
    basis: 'stat-check',
    value: 10 + floor,
    breakdown: `10 + ${floor} (Floor)`,
  };
}

export function gmAssignedDifficulty(value: number): DifficultyResult {
  return { basis: 'gm-assigned', value, breakdown: 'Assigned by the GM' };
}

function assertFloor(floor: number): void {
  if (!Number.isInteger(floor) || floor < 1) {
    throw new Error(`Floor Number must be an integer of at least 1, received ${floor}`);
  }
}
